import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, User, Phone, MapPin, Calendar, IndianRupee, Save, UserCircle, Users, Briefcase, Mail, CheckCircle, Loader2, Bookmark, BookmarkCheck, Building2, ExternalLink, Clock, X } from 'lucide-react';
import axios from 'axios';

const OCCUPATION_OPTIONS = [
  { key: 'Agriculture', label: 'Agriculture/Farming' },
  { key: 'IT', label: 'IT/Technology' },
  { key: 'Banking', label: 'Banking/Finance' },
  { key: 'Business', label: 'Business/Self-employed' },
  { key: 'Accountant', label: 'Accountant' },
  { key: 'Home Maker', label: 'Homemaker' },
  { key: 'Government or Civil Services', label: 'Government Service' },
  { key: 'Health Care', label: 'Healthcare' },
  { key: 'Traditional Crafts or Handicraft', label: 'Handicrafts/Artisan' },
  { key: 'Manual Labor', label: 'Daily Wage Worker' },
  { key: 'Creative Media', label: 'Arts/Media' },
  { key: 'Student', label: 'Student' },
  { key: 'Sales or Retail Workers', label: 'Sales/Retail' },
  { key: 'Teachers', label: 'Teacher/Professor' },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpEmail, setOtpEmail] = useState(''); // Track the exact email OTP was sent to
  const sendingRef = useRef(false); // Guard against double-clicks
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [formData, setFormData] = useState({
    name: '',
    district: '',
    age: '',
    caste: '',
    occupation: '',
    household_income: ''
  });

  // Saved government jobs state
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedJobsLoading, setSavedJobsLoading] = useState(false);

  // Buried "data preferences" toggle (intentionally low-visibility)
  const [dataPrefsOpen, setDataPrefsOpen] = useState(false);
  const [dataPrefSaving, setDataPrefSaving] = useState(false);
  const [dataPrefMessage, setDataPrefMessage] = useState(null); // {type, text, log_id}
  const [confirmOptOutOpen, setConfirmOptOutOpen] = useState(false);
  const optedIn = user?.training_data_opted_in !== false; // default true

  const performOptToggle = async (nextState) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setDataPrefSaving(true);
    setDataPrefMessage(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/training-data-preference`,
        { opted_in: nextState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh user from backend to ensure context is in sync
      await refreshUser();
      setDataPrefMessage({
        type: 'success',
        text: res.data?.message || 'Preference saved.',
        log_id: res.data?.log_id,
      });
    } catch (err) {
      console.error('Error updating data preference:', err);
      setDataPrefMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Could not update preference. Please try again.',
      });
    } finally {
      setDataPrefSaving(false);
    }
  };

  const handleToggleOptIn = () => {
    if (dataPrefSaving) return;
    if (optedIn) {
      // Opt-OUT requires confirmation
      setConfirmOptOutOpen(true);
    } else {
      // Opt-IN goes through directly
      performOptToggle(true);
    }
  };

  const handleConfirmOptOut = async () => {
    setConfirmOptOutOpen(false);
    await performOptToggle(false);
  };

  const downloadConsentPdf = (logId) => {
    const token = localStorage.getItem('token');
    if (!token || !logId) return;
    // Use fetch with auth header → blob download
    fetch(`${API_URL}/api/auth/consent-pdf/${logId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KMS_Consent_${logId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(err => console.error('PDF download failed:', err));
  };

  // Refresh user data from backend on mount to get latest email_verified status
  useEffect(() => {
    refreshUser();
  }, []);

  // Fetch saved government jobs
  useEffect(() => {
    const fetchSavedJobs = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setSavedJobsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/jobs/saved`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedJobs(res.data.saved_jobs || []);
      } catch (err) {
        console.error('Error fetching saved jobs:', err);
      } finally {
        setSavedJobsLoading(false);
      }
    };
    fetchSavedJobs();
  }, [API_URL]);

  const handleUnsaveJob = async (jobId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.delete(`${API_URL}/api/jobs/save/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedJobs(prev => prev.filter(j => j.job_id !== jobId));
    } catch (err) {
      console.error('Error unsaving job:', err);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        district: user.district || '',
        age: user.age || '',
        caste: user.caste || '',
        occupation: user.occupation || '',
        household_income: user.household_income || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendVerification = async () => {
    if (sendingRef.current) return; // Prevent double-clicks
    const email = (emailInput || user.email || '').trim();
    if (!email || !email.includes('@')) {
      setEmailMessage('Please enter a valid email address');
      return;
    }
    sendingRef.current = true;
    setEmailSending(true);
    setEmailMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/email/send-verification`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOtpEmail(email); // Store the exact email used
      setOtpSent(true);
      setOtpInput(''); // Clear any previous OTP input
      setEmailMessage('OTP sent! Check your email.');
      setUser({ ...user, email, email_verified: false });
    } catch (err) {
      setEmailMessage(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setEmailSending(false);
      sendingRef.current = false;
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpInput || otpInput.length !== 6) {
      setEmailMessage('Enter the 6-digit OTP');
      return;
    }
    setOtpVerifying(true);
    setEmailMessage('');
    try {
      const token = localStorage.getItem('token');
      const email = otpEmail || emailInput || user.email;
      await axios.post(`${API_URL}/api/email/verify-otp`, { email, otp: otpInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailMessage('Email verified successfully!');
      setOtpSent(false);
      setOtpInput('');
      setOtpEmail('');
      setUser({ ...user, email, email_verified: true });
    } catch (err) {
      setEmailMessage(err.response?.data?.detail || 'Invalid OTP. Please try again or resend.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          district: formData.district,
          age: formData.age,
          caste: formData.caste,
          occupation: formData.occupation,
          household_income: formData.household_income
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (setUser) {
          setUser(data.user);
        }
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const formatCaste = (caste) => {
    if (!caste) return 'Not provided';
    const casteMap = {
      'general': 'General',
      'obc': 'OBC (Other Backward Class)',
      'sc': 'SC (Scheduled Caste)',
      'st': 'ST (Scheduled Tribe)',
      'ews': 'EWS (Economically Weaker Section)',
      'other': 'Other'
    };
    return casteMap[caste] || caste;
  };

  const formatIncome = (income) => {
    if (!income) return 'Not provided';
    const incomeMap = {
      'below_1_lakh': 'Below ₹1 Lakh',
      '1_to_3_lakh': '₹1 - 3 Lakh',
      '3_to_5_lakh': '₹3 - 5 Lakh',
      '5_to_10_lakh': '₹5 - 10 Lakh',
      'above_10_lakh': 'Above ₹10 Lakh'
    };
    return incomeMap[income] || income;
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:text-gray-900 text-xs sm:text-sm px-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">{t('back')}</span> {t('home')}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-2xl">
        {/* Profile Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
            <UserCircle className="w-10 h-10 sm:w-16 sm:h-16 text-white" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{user.name}</h1>
          <p className="text-sm sm:text-base text-gray-500">{user.phone}</p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg text-sm sm:text-base ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Profile Card */}
        <Card className="bg-white shadow-xl rounded-xl sm:rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 sm:px-6 py-3 sm:py-4">
            <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              {t('profileInfo')}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Name (Read-only) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('fullName')}
              </label>
              <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 rounded-lg text-sm sm:text-base text-gray-600">{user.name || t('notProvided')}</p>
            </div>

            {/* Phone (Read-only) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('phoneNumber')}
              </label>
              <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 rounded-lg text-sm sm:text-base text-gray-600">{user.phone}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{t('phoneCannotChange')}</p>
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('emailAddress')}
                {user.email_verified && (
                  <span className="ml-2 inline-flex items-center text-green-600 text-xs">
                    <CheckCircle className="w-3 h-3 mr-0.5" /> {t('emailVerified')}
                  </span>
                )}
              </label>
              {user.email && user.email_verified ? (
                <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg text-sm sm:text-base text-gray-900">{user.email}</p>
              ) : otpSent ? (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">OTP sent to <strong>{otpEmail || emailInput || user.email}</strong></p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={otpInput}
                      onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder={t('enterOtp')}
                      maxLength={6}
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center tracking-widest font-mono text-lg"
                      data-testid="profile-otp-input"
                    />
                    <Button
                      onClick={handleVerifyOtp}
                      disabled={otpVerifying || otpInput.length !== 6}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm px-4"
                      data-testid="verify-otp-btn"
                    >
                      {otpVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('verifyOtp')}
                    </Button>
                  </div>
                  <button onClick={handleSendVerification} className="text-xs text-green-600 hover:underline" data-testid="resend-otp-btn">
                    {emailSending ? t('sending') : t('resendOtp')}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailInput || user.email || ''}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="profile-email-input"
                  />
                  <Button
                    onClick={handleSendVerification}
                    disabled={emailSending}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm px-3"
                    data-testid="send-otp-btn"
                  >
                    {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('sendOtp')}
                  </Button>
                </div>
              )}
              {emailMessage && (
                <p className={`text-xs mt-1 ${emailMessage.includes('sent') || emailMessage.includes('verified') || emailMessage.includes('success') ? 'text-green-600' : 'text-red-500'}`}>{emailMessage}</p>
              )}
              {user.email && !user.email_verified && !otpSent && (
                <p className="text-[10px] sm:text-xs text-orange-500 mt-1">Email not verified — click "Send OTP" to verify</p>
              )}
            </div>

            {/* District */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('district')}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter your district"
                />
              ) : (
                <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg text-sm sm:text-base text-gray-900">{user.district || t('notProvided')}</p>
              )}
            </div>

            {/* Age */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('age')}
              </label>
              {isEditing ? (
                <select
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select age range</option>
                  <option value="18-24">18 – 24</option>
                  <option value="25-34">25 – 34</option>
                  <option value="35-44">35 – 44</option>
                  <option value="45-54">45 – 54</option>
                  <option value="55-64">55 – 64</option>
                  <option value="65+">65+</option>
                </select>
              ) : (
                <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg text-sm sm:text-base text-gray-900">{user.age || t('notProvided')}</p>
              )}
            </div>

            {/* Occupation */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('occupation')}
              </label>
              {isEditing ? (
                <select
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="profile-occupation-select"
                >
                  <option value="">Select Occupation</option>
                  {OCCUPATION_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg text-sm sm:text-base text-gray-900">
                  {OCCUPATION_OPTIONS.find(o => o.key === user.occupation)?.label || user.occupation || t('notProvided')}
                </p>
              )}
            </div>

            {/* Caste/Category (Read-only) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                Caste / Category
              </label>
              <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 rounded-lg text-sm sm:text-base text-gray-600">
                {formatCaste(user.caste)}
              </p>
            </div>

            {/* Household Income */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <IndianRupee className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1.5 sm:mr-2" />
                {t('annualIncome')}
              </label>
              {isEditing ? (
                <select
                  name="household_income"
                  value={formData.household_income}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select income range</option>
                  <option value="below_1_lakh">Below ₹1 Lakh</option>
                  <option value="1_to_3_lakh">₹1 - 3 Lakh</option>
                  <option value="3_to_5_lakh">₹3 - 5 Lakh</option>
                  <option value="5_to_10_lakh">₹5 - 10 Lakh</option>
                  <option value="above_10_lakh">Above ₹10 Lakh</option>
                </select>
              ) : (
                <p className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg text-sm sm:text-base text-gray-900">
                  {formatIncome(user.household_income)}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        name: user.name || '',
                        district: user.district || '',
                        age: user.age || '',
                        caste: user.caste || '',
                        household_income: user.household_income || ''
                      });
                    }}
                    className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 sm:py-3 text-sm sm:text-base"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? t('saving') : t('saveChanges')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 sm:py-3 text-sm sm:text-base"
                >
                  {t('editProfile')}
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* Saved Government Jobs Section */}
        <Card className="p-4 sm:p-6 shadow-lg rounded-xl border-0 mt-6" data-testid="saved-jobs-section">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            {t('savedGovJobs')}
          </h3>
          {savedJobsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : savedJobs.length > 0 ? (
            <div className="space-y-3">
              {savedJobs.map((job) => {
                const daysLeft = job.last_date ? Math.ceil((new Date(job.last_date) - new Date()) / (1000*60*60*24)) : null;
                const deadlineColor = daysLeft === null ? 'text-gray-500' : daysLeft < 0 ? 'text-red-600' : daysLeft <= 5 ? 'text-red-600' : daysLeft <= 15 ? 'text-amber-600' : 'text-emerald-600';
                return (
                  <div key={job.job_id} className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`saved-job-${job.job_id}`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-slate-800 truncate">{job.title}</h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{job.organisation}</span>
                        {job.last_date && (
                          <span className={`flex items-center gap-1 ${deadlineColor} font-medium`}>
                            <Clock className="w-3 h-3" />{daysLeft !== null && daysLeft >= 0 ? `${daysLeft} ${t('daysLeft')}` : t('expired')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {job.apply_url && (
                        <a href={job.apply_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-blue-300 text-blue-700">
                            <ExternalLink className="w-3 h-3 mr-1" /> {t('applyNow')}
                          </Button>
                        </a>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleUnsaveJob(job.job_id)} data-testid={`unsave-job-${job.job_id}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6" data-testid="no-saved-jobs">
              <Bookmark className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('noSavedJobsYet')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('saveJobsHint')}</p>
            </div>
          )}
        </Card>

        {/* Data preferences — intentionally low-visibility footer link */}
        <div className="mt-10 mb-2 text-center">
          <button
            type="button"
            onClick={() => setDataPrefsOpen(v => !v)}
            className="text-[10px] text-gray-300 hover:text-gray-400 underline underline-offset-2 tracking-wide"
            data-testid="data-prefs-toggle-link"
            aria-label="Manage data preferences"
          >
            {dataPrefsOpen ? 'hide preferences' : 'preferences'}
          </button>
          {dataPrefsOpen && (
            <div className="mt-3 mx-auto max-w-md text-left bg-gray-50/40 border border-gray-100 rounded-md p-3" data-testid="data-prefs-panel">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-[11px] font-medium text-gray-500">Anonymous training data</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">
                    Help improve KMS Smart with anonymised, aggregated usage signals (occupation, location, language). No personal info is shared. Toggling generates a revised T&amp;C consent record (PDF).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleOptIn}
                  disabled={dataPrefSaving}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${optedIn ? 'bg-green-500' : 'bg-gray-300'} ${dataPrefSaving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                  data-testid="data-prefs-opt-toggle"
                  aria-pressed={optedIn}
                  aria-label={optedIn ? 'Opt out of training data collection' : 'Opt in to training data collection'}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${optedIn ? 'translate-x-4' : 'translate-x-0.5'}`}
                  />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2" data-testid="data-prefs-status">
                Status: <span className={optedIn ? 'text-green-600' : 'text-gray-500'}>{optedIn ? 'Opted in' : 'Opted out'}</span>
              </p>
              {dataPrefSaving && (
                <p className="text-[10px] text-gray-400 mt-1" data-testid="data-prefs-saving">Saving…</p>
              )}
              {dataPrefMessage && (
                <div
                  className={`mt-2 text-[10px] rounded p-2 ${dataPrefMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                  data-testid="data-prefs-message"
                >
                  <p>{dataPrefMessage.text}</p>
                  {dataPrefMessage.log_id && (
                    <button
                      type="button"
                      onClick={() => downloadConsentPdf(dataPrefMessage.log_id)}
                      className="mt-1 underline underline-offset-2 hover:text-green-800"
                      data-testid="data-prefs-download-pdf"
                    >
                      Download revised T&amp;C consent record (PDF)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Opt-out confirmation dialog */}
        {confirmOptOutOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center" data-testid="opt-out-confirm-modal">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmOptOutOpen(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900">Opt out of training data?</h3>
              <p className="mt-2 text-sm text-gray-600">
                You're about to opt out of anonymous training data collection. By confirming, KMS will:
              </p>
              <ul className="mt-2 text-sm text-gray-600 list-disc pl-5 space-y-1">
                <li>Mark all your future KMS Smart responses as <b>opted out</b>.</li>
                <li>Generate a revised Terms &amp; Conditions consent PDF reflecting this change.</li>
                <li>Keep this preference until you change it again.</li>
              </ul>
              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmOptOutOpen(false)}
                  data-testid="opt-out-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleConfirmOptOut}
                  data-testid="opt-out-confirm-btn"
                >
                  Yes, opt out
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
